import {
  Bug,
  Camera,
  CheckCircle2,
  HelpCircle,
  Lightbulb,
  Loader2,
  MessageSquarePlus,
  Paperclip,
  Send,
  Upload,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  fetchFeedbackConfig,
  fetchFeedbackDetail,
  fetchMyFeedback,
  replyToFeedback,
  submitFeedback,
  type FeedbackConfig,
  type FeedbackItem,
  type FeedbackKind,
} from "../../../lib/feedback";
import { ScreenshotRedactor } from "./ScreenshotRedactor";
import { env } from "../../../lib/env";

const KIND_OPTIONS: Array<{
  id: FeedbackKind;
  label: string;
  description: string;
  icon: typeof Bug;
}> = [
  { id: "bug", label: "Something is broken", description: "Unexpected behavior or an error", icon: Bug },
  { id: "improvement", label: "Suggest an improvement", description: "A better or easier way to work", icon: Lightbulb },
  { id: "question", label: "Ask a question", description: "Something is unclear", icon: HelpCircle },
];

export function FeedbackLauncher({ workspaceId, dark = false }: { workspaceId: string | null; dark?: boolean }) {
  const [config, setConfig] = useState<FeedbackConfig | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    fetchFeedbackConfig()
      .then((value) => !cancelled && setConfig(value))
      .catch(() => !cancelled && setConfig(null));
    return () => { cancelled = true; };
  }, [workspaceId]);

  useEffect(() => {
    const listener = () => setOpen(true);
    window.addEventListener("meridian:feedback-open", listener);
    return () => window.removeEventListener("meridian:feedback-open", listener);
  }, []);

  useEffect(() => {
    if (config?.enabled && new URLSearchParams(window.location.search).has("feedback")) setOpen(true);
  }, [config?.enabled]);

  if (!workspaceId || !config?.enabled) return null;
  const requestedFeedbackId = new URLSearchParams(window.location.search).get("feedback");

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={[
          "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] transition-colors",
          dark ? "text-slate-400 hover:bg-white/5 hover:text-white" : "text-slate-500 hover:bg-slate-50 hover:text-slate-950",
        ].join(" ")}
      >
        <MessageSquarePlus className="h-4 w-4 shrink-0" strokeWidth={1.6} />
        Feedback
      </button>
      {open && (
        <FeedbackDrawer
          workspaceId={workspaceId}
          maxScreenshotMb={config.max_screenshot_mb}
          initialFeedbackId={requestedFeedbackId}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function FeedbackDrawer({
  workspaceId,
  maxScreenshotMb,
  initialFeedbackId,
  onClose,
}: {
  workspaceId: string;
  maxScreenshotMb: number;
  initialFeedbackId: string | null;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<"send" | "history">(initialFeedbackId ? "history" : "send");
  const [kind, setKind] = useState<FeedbackKind>("bug");
  const [description, setDescription] = useState("");
  const [blocker, setBlocker] = useState(false);
  const [technical, setTechnical] = useState(true);
  const [source, setSource] = useState<Blob | null>(null);
  const [prepared, setPrepared] = useState<Blob | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<FeedbackItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const prepare = useCallback((blob: Blob) => setPrepared(blob), []);

  useEffect(() => {
    function paste(event: ClipboardEvent) {
      const image = Array.from(event.clipboardData?.items ?? []).find((item) => item.type.startsWith("image/"));
      const file = image?.getAsFile();
      if (file) acceptScreenshot(file);
    }
    window.addEventListener("paste", paste);
    return () => window.removeEventListener("paste", paste);
  });

  function acceptScreenshot(file: Blob) {
    setError(null);
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }
    if (file.size > maxScreenshotMb * 1024 * 1024) {
      setError(`Screenshot must be ${maxScreenshotMb} MB or smaller.`);
      return;
    }
    setSource(file);
    setPrepared(file);
  }

  async function captureScreen() {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      const video = document.createElement("video");
      video.srcObject = stream;
      await video.play();
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext("2d")?.drawImage(video, 0, 0);
      stream.getTracks().forEach((track) => track.stop());
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
      if (blob) acceptScreenshot(blob);
    } catch (cause) {
      if ((cause as DOMException).name !== "NotAllowedError") setError("Screen capture did not work. You can upload or paste an image instead.");
    }
  }

  async function sendFeedback() {
    if (description.trim().length < 10) {
      setError("Please add a little more detail (at least 10 characters).");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const item = await submitFeedback(workspaceId, {
        kind,
        description: description.trim(),
        reportedBlocker: kind === "bug" && blocker,
        technicalDetailsIncluded: technical,
        screenshot: prepared,
      });
      setSubmitted(item);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not send feedback.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[80] flex justify-end bg-slate-950/30" role="dialog" aria-modal="true" aria-label="Send feedback">
      <button type="button" aria-label="Close feedback" className="absolute inset-0 cursor-default" onClick={onClose} />
      <section className="relative flex h-full w-full max-w-[520px] flex-col bg-white shadow-2xl">
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-[16px] font-semibold text-slate-950">Help improve Meridian</h2>
            <p className="mt-0.5 text-[12px] text-slate-500">Your report goes directly to the Meridian development inbox.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="flex border-b border-slate-100 px-5">
          {(["send", "history"] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setTab(value)}
              className={`mr-5 border-b-2 py-2.5 text-[12.5px] font-medium ${tab === value ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400"}`}
            >
              {value === "send" ? "Send feedback" : "Your feedback"}
            </button>
          ))}
        </div>
        {tab === "history" ? (
          <FeedbackHistory workspaceId={workspaceId} initialId={initialFeedbackId} />
        ) : submitted ? (
          <Success item={submitted} onAnother={() => { setSubmitted(null); setDescription(""); setSource(null); setPrepared(null); }} onHistory={() => setTab("history")} />
        ) : (
          <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
            <fieldset>
              <legend className="mb-2 text-[12px] font-semibold text-slate-700">What would you like to share?</legend>
              <div className="grid gap-2">
                {KIND_OPTIONS.map(({ id, label, description: hint, icon: Icon }) => (
                  <button key={id} type="button" onClick={() => setKind(id)} className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left ${kind === id ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300"}`}>
                    <Icon className="h-4 w-4 text-slate-600" />
                    <span><span className="block text-[12.5px] font-medium text-slate-900">{label}</span><span className="block text-[11px] text-slate-500">{hint}</span></span>
                  </button>
                ))}
              </div>
            </fieldset>
            <label className="block">
              <span className="mb-1.5 block text-[12px] font-semibold text-slate-700">Tell us what happened or what would be better</span>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={5} maxLength={5000} placeholder={kind === "bug" ? "What did you expect, and what happened instead?" : "Describe the change and why it would help…"} className="w-full resize-y rounded-xl border border-slate-200 px-3 py-2.5 text-[13px] outline-none focus:border-slate-500" />
              <span className="mt-1 block text-right text-[10.5px] text-slate-400">{description.length}/5000</span>
            </label>
            {kind === "bug" && (
              <label className="flex items-start gap-2.5 rounded-lg bg-amber-50 px-3 py-2.5 text-[12px] text-amber-950">
                <input type="checkbox" checked={blocker} onChange={(e) => setBlocker(e.target.checked)} className="mt-0.5" />
                <span><strong className="font-semibold">This blocks my work</strong><span className="mt-0.5 block text-[11px] text-amber-800">Use this when you cannot continue, not just when something is inconvenient.</span></span>
              </label>
            )}
            <div>
              <p className="mb-2 text-[12px] font-semibold text-slate-700">Screenshot <span className="font-normal text-slate-400">(optional)</span></p>
              {!source ? (
                <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); const file = e.dataTransfer.files[0]; if (file) acceptScreenshot(file); }} className="rounded-xl border border-dashed border-slate-300 p-4 text-center">
                  <Paperclip className="mx-auto h-5 w-5 text-slate-400" />
                  <p className="mt-1 text-[11.5px] text-slate-500">Paste, drag an image here, or</p>
                  <div className="mt-2 flex justify-center gap-2">
                    <button type="button" onClick={() => void captureScreen()} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[11.5px] font-medium text-slate-700 hover:bg-slate-50"><Camera className="h-3.5 w-3.5" /> Capture screen</button>
                    <button type="button" onClick={() => fileInput.current?.click()} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[11.5px] font-medium text-slate-700 hover:bg-slate-50"><Upload className="h-3.5 w-3.5" /> Upload</button>
                  </div>
                  <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) acceptScreenshot(file); }} />
                </div>
              ) : (
                <div className="space-y-2">
                  <ScreenshotRedactor source={source} onPrepared={prepare} />
                  <button type="button" onClick={() => { setSource(null); setPrepared(null); }} className="text-[11px] font-medium text-rose-600">Remove screenshot</button>
                </div>
              )}
            </div>
            <details className="rounded-lg border border-slate-200 px-3 py-2">
              <summary className="cursor-pointer text-[11.5px] font-medium text-slate-700">Technical details</summary>
              <label className="mt-2 flex items-center gap-2 text-[11px] text-slate-600"><input type="checkbox" checked={technical} onChange={(e) => setTechnical(e.target.checked)} /> Include these details</label>
              <dl className="mt-2 grid grid-cols-2 gap-2 text-[10px] text-slate-500"><div><dt className="font-medium">Page</dt><dd className="truncate">{window.location.pathname}</dd></div><div><dt className="font-medium">App version</dt><dd>{import.meta.env.VITE_APP_VERSION ?? "web"}</dd></div><div><dt className="font-medium">Browser</dt><dd className="truncate" title={navigator.userAgent}>{navigator.userAgent}</dd></div><div><dt className="font-medium">Screen</dt><dd>{window.innerWidth} × {window.innerHeight}</dd></div></dl>
            </details>
            <p className="text-[10.5px] leading-relaxed text-slate-400">Your report is shared with Meridian support and may contain customer data. Screenshots stay private, are stripped of metadata, and are automatically deleted after the configured retention period. Screenshot pixels are never sent to AI triage.</p>
            {error && <p role="alert" className="rounded-lg bg-rose-50 px-3 py-2 text-[11.5px] text-rose-700">{error}</p>}
          </div>
        )}
        {!submitted && tab === "send" && (
          <footer className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
            <button type="button" onClick={onClose} className="rounded-lg px-3 py-2 text-[12px] font-medium text-slate-600 hover:bg-slate-100">Cancel</button>
            <button type="button" onClick={() => void sendFeedback()} disabled={submitting} className="inline-flex items-center gap-1.5 rounded-lg bg-slate-950 px-3.5 py-2 text-[12px] font-semibold text-white disabled:opacity-50">
              {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send feedback
            </button>
          </footer>
        )}
      </section>
    </div>
  );
}

function Success({ item, onAnother, onHistory }: { item: FeedbackItem; onAnother: () => void; onHistory: () => void }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><CheckCircle2 className="h-6 w-6" /></span>
      <h3 className="mt-4 text-[16px] font-semibold text-slate-950">Thanks — we have it</h3>
      <p className="mt-1 text-[12px] text-slate-500">Reference <strong className="font-semibold text-slate-700">{item.reference}</strong>. We’ll reply here if we need more information.</p>
      <div className="mt-5 flex gap-2"><button type="button" onClick={onAnother} className="rounded-lg border border-slate-200 px-3 py-2 text-[12px] font-medium">Send another</button><button type="button" onClick={onHistory} className="rounded-lg bg-slate-950 px-3 py-2 text-[12px] font-medium text-white">View your feedback</button></div>
    </div>
  );
}

function FeedbackHistory({ workspaceId, initialId }: { workspaceId: string; initialId: string | null }) {
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [selected, setSelected] = useState<FeedbackItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await fetchMyFeedback(workspaceId)).items); } finally { setLoading(false); }
  }, [workspaceId]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!initialId) return;
    fetchFeedbackDetail(workspaceId, initialId).then(setSelected).catch(() => undefined);
  }, [initialId, workspaceId]);

  async function choose(item: FeedbackItem) {
    setSelected(await fetchFeedbackDetail(workspaceId, item.id));
  }
  async function sendReply() {
    if (!selected || !reply.trim()) return;
    setSelected(await replyToFeedback(workspaceId, selected.id, reply.trim()));
    setReply("");
  }

  if (loading) return <div className="flex flex-1 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  if (selected) return (
    <div className="flex-1 overflow-y-auto px-5 py-5">
      <button type="button" onClick={() => setSelected(null)} className="text-[11.5px] font-medium text-slate-500">← All feedback</button>
      <div className="mt-3 flex items-center gap-2"><strong className="text-[13px]">{selected.reference}</strong><Status status={selected.status} /></div>
      <p className="mt-3 whitespace-pre-wrap text-[13px] leading-relaxed text-slate-700">{selected.description}</p>
      {selected.attachment && <CustomerAttachment workspaceId={workspaceId} item={selected} />}
      <div className="mt-5 space-y-3 border-t border-slate-100 pt-4">
        {selected.events.filter((event) => event.message).map((event) => <div key={event.id} className="rounded-lg bg-slate-50 px-3 py-2"><p className="text-[11px] font-medium text-slate-500">{event.event_type.includes("developer") ? "Meridian" : "You"} · {new Date(event.created_at).toLocaleString()}</p><p className="mt-1 whitespace-pre-wrap text-[12px] text-slate-700">{event.message}</p></div>)}
      </div>
      <div className="mt-4 flex gap-2"><input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Add more information…" className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-2 text-[12px]" /><button type="button" onClick={() => void sendReply()} className="rounded-lg bg-slate-950 px-3 text-[11.5px] font-medium text-white">Reply</button></div>
    </div>
  );
  return <div className="flex-1 overflow-y-auto px-5 py-4">{items.length === 0 ? <p className="py-12 text-center text-[12px] text-slate-400">You haven’t sent any feedback yet.</p> : <div className="space-y-2">{items.map((item) => <button key={item.id} type="button" onClick={() => void choose(item)} className="w-full rounded-xl border border-slate-200 px-3 py-3 text-left hover:bg-slate-50"><div className="flex items-center justify-between"><span className="text-[12px] font-semibold text-slate-900">{item.reference}</span><Status status={item.status} /></div><p className="mt-1 line-clamp-2 text-[11.5px] text-slate-600">{item.description}</p><p className="mt-2 text-[10.5px] text-slate-400">{new Date(item.created_at).toLocaleDateString()}</p></button>)}</div>}</div>;
}

function Status({ status }: { status: string }) {
  const labels: Record<string, string> = { new: "Received", needs_info: "More information needed", reviewing: "Under review", planned: "Planned", in_progress: "In progress", resolved: "Shipped", closed: "Closed" };
  return <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">{labels[status] ?? status}</span>;
}

function CustomerAttachment({ workspaceId, item }: { workspaceId: string; item: FeedbackItem }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!item.attachment) return;
    let current: string | null = null;
    fetch(`${env.apiBaseUrl}/api/v1/workspaces/${workspaceId}/feedback/${item.id}/attachment/${item.attachment.id}`, { credentials: "include", headers: { "X-Requested-With": "fetch" } })
      .then((response) => response.ok ? response.blob() : Promise.reject(new Error("Screenshot unavailable")))
      .then((blob) => { current = URL.createObjectURL(blob); setUrl(current); })
      .catch(() => undefined);
    return () => { if (current) URL.revokeObjectURL(current); };
  }, [item, workspaceId]);
  return url ? <img src={url} alt={`Screenshot for ${item.reference}`} className="mt-4 max-h-64 w-full rounded-xl border border-slate-200 object-contain" /> : null;
}
